import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Search, User, Kanban, Loader2, X, CornerDownLeft, Zap, Clock, Plus, Settings, Moon, Sparkles } from 'lucide-react';
import { useT } from '../utils/translate';
import { fmtPhone } from '../utils/format';
import { usePermissions } from '../utils/permissions';
import { srv } from '../utils/serverText';
import { NAV_ITEMS } from '../config/navItems';
import { useAppearance } from '../utils/appearance';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const RECENT_KEY = 'miko-recent';
const NAV_PATH = { dashboard: '/dashboard', contacts: '/contacts', tasks: '/tasks', education: '/education', inbox: '/inbox', calls: '/calls', reviews: '/reviews', analytics: '/analytics/sales' };

const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, 5); } catch { return []; } };
const saveRecent = (it) => {
  try {
    const rec = [{ key: it.key, kind: it.kind, title: it.title, sub: it.sub, to: it.to }, ...readRecent().filter(r => r.key !== it.key)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(rec));
  } catch { /* приватный режим: «недавние» просто не сохранятся */ }
};

const Kbd = ({ children }) => (
  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md border border-surface-200 bg-card text-xs font-medium text-ink-secondary font-sans">{children}</span>
);

// Командная палитра и глобальный поиск: Ctrl/⌘+K или кнопка в шапке.
// Пустой запрос: недавние переходы и команды (разделы, воронки, «Создать…», тема). Запрос: подходящие команды + клиенты и сделки.
// Закрывается Esc, крестиком или кликом вне окна.
export default function GlobalSearch({ open, onClose }) {
  const t = useT();
  const navigate = useNavigate();
  const { canSeeModule, can } = usePermissions();
  const funnels = useSelector((s) => s.funnels.list);
  const user = useSelector((s) => s.auth.user);
  const appearance = useAppearance();
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [idx, setIdx] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const canRef = useRef(canSeeModule);   // canSeeModule может менять ссылку между рендерами, в зависимости эффекта его не берём
  canRef.current = canSeeModule;
  const seq = useRef(0);

  useEffect(() => {
    if (open) { setQ(''); setContacts([]); setDeals([]); setIdx(0); setRecent(readRecent()); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const query = q.trim();
    if (query.length < 2) { setContacts([]); setDeals([]); setLoading(false); return undefined; }
    const my = ++seq.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      const [c, d] = await Promise.all([
        canRef.current('contacts') ? axios.get(`${API}/contacts`, { params: { search: query, page: 1, limit: 6 } }).then(r => r.data.contacts || []).catch(() => []) : [],
        canRef.current('funnels') ? axios.get(`${API}/funnels/deals/search`, { params: { q: query, limit: 6 } }).then(r => r.data.deals || []).catch(() => []) : [],
      ]);
      if (my !== seq.current) return;   // пришёл ответ на устаревший запрос
      setContacts(c); setDeals(d); setIdx(0); setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [q, open]);

  // Команды, доступные этому пользователю
  const commands = useMemo(() => {
    const list = [];
    NAV_ITEMS.filter(i => canSeeModule(i.key)).forEach(i => list.push({ key: `go-${i.key}`, kind: 'cmd', icon: i.icon, title: t(i.labelKey), sub: t('topbar.cmdGo'), to: NAV_PATH[i.key] }));
    if (canSeeModule('funnels')) funnels.forEach(f => list.push({ key: `fn-${f._id}`, kind: 'cmd', icon: Kanban, title: f.name, sub: t('topbar.cmdFunnel'), to: `/funnel/${f._id}` }));
    if (canSeeModule('funnels') && can('funnels', 'create') && funnels[0]) list.push({ key: 'new-deal', kind: 'cmd', icon: Plus, title: t('topbar.createDeal'), sub: funnels[0].name, to: `/funnel/${funnels[0]._id}/deal/new` });
    if (canSeeModule('contacts') && can('contacts', 'create')) list.push({ key: 'new-contact', kind: 'cmd', icon: Plus, title: t('topbar.createContact'), sub: '', to: '/contacts/new' });
    if (['owner', 'admin'].includes(user?.role)) list.push({ key: 'settings', kind: 'cmd', icon: Settings, title: t('topbar.settings'), sub: t('topbar.cmdGo'), to: '/settings' });
    list.push({ key: 'theme', kind: 'cmd', icon: Moon, title: t('topbar.cmdTheme'), sub: t('topbar.appearance'), run: () => appearance.setThemeMode(appearance.theme === 'dark' ? 'light' : 'dark') });
    list.push({ key: 'lite', kind: 'cmd', icon: Sparkles, title: t('topbar.liteMode'), sub: appearance.lite ? '✓' : '', run: () => appearance.setLite(!appearance.lite) });
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeModule, can, funnels, user, appearance.theme, appearance.lite, t]);

  const query = q.trim();
  const typed = query.length >= 2;
  const items = useMemo(() => {
    const found = [
      ...contacts.map(c => ({ key: `c-${c._id}`, kind: 'contact', title: srv(c.name) || fmtPhone(c.phone), sub: c.name ? fmtPhone(c.phone) : '', to: `/contacts/${c._id}` })),
      ...deals.map(d => ({ key: `d-${d._id}`, kind: 'deal', title: srv(d.title), sub: '', to: d.funnel ? `/funnel/${d.funnel}/deal/${d._id}` : null })),
    ].filter(i => i.to);
    if (!query) return [...recent.map(r => ({ ...r, kind: 'recent' })), ...commands];
    const low = query.toLowerCase();
    const cmds = commands.filter(c => c.title.toLowerCase().includes(low)).slice(0, 5);
    return [...cmds, ...found];
  }, [contacts, deals, commands, recent, query]);

  // активная строка всегда в зоне видимости при навигации стрелками
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [idx, items]);

  if (!open) return null;

  const go = (item) => {
    if (!item) return;
    onClose();
    if (item.run) { item.run(); return; }
    if (item.kind !== 'recent') saveRecent(item);
    navigate(item.to);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(items[idx]); }
  };

  const groups = [
    ['recent', t('topbar.recent'), Clock, 'bg-surface-100 text-ink-secondary'],
    ['cmd', t('topbar.cmdGroup'), Zap, 'bg-primary-50 text-primary-700'],
    ['contact', t('topbar.searchContacts'), User, 'bg-primary-50 text-primary-700'],
    ['deal', t('topbar.searchDeals'), Kanban, 'bg-amber-50 text-amber-700'],
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[10vh] px-4" data-dialog-skip role="dialog" aria-modal="true" aria-label={t('topbar.search')}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[3px]" onClick={onClose} />
      <div className="relative w-full max-w-2xl glass-strong rounded-3xl shadow-pop overflow-hidden flex flex-col max-h-[70vh] animate-pop-in">

        {/* Строка поиска */}
        <div className="flex items-center gap-3 px-5 h-16 border-b border-surface-200 shrink-0">
          <Search className="w-5 h-5 text-ink-tertiary shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={e => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={onKeyDown}
            placeholder={t('topbar.searchPlaceholder')}
            aria-label={t('topbar.search')}
            autoComplete="off"
            className="flex-1 min-w-0 h-full text-base text-ink placeholder:text-ink-placeholder bg-transparent !border-0 !shadow-none !ring-0 !outline-none focus:!ring-0 focus:!border-0 p-0"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-primary-600 shrink-0" />}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('topbar.close')}
            title={t('topbar.close')}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-tertiary hover:text-ink hover:bg-surface-100 transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Результаты */}
        <div ref={listRef} className="flex-1 overflow-y-auto min-h-[120px]">
          {query && !typed && items.length === 0 && (
            <div className="px-5 py-10 text-center"><p className="text-sm text-ink-secondary">{t('topbar.searchHint')}</p></div>
          )}
          {typed && !loading && items.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm text-ink-secondary">{t('topbar.searchEmpty')}</p>
            </div>
          )}
          {groups.map(([kind, label, Icon, tone]) => {
            const rows = items.filter(i => i.kind === kind);
            if (!rows.length) return null;
            return (
              <div key={kind} className="py-2">
                <p className="px-5 pb-1.5 text-xs font-semibold text-ink-tertiary uppercase tracking-wide">{label}</p>
                <div className="px-2">
                  {rows.map(item => {
                    const i = items.indexOf(item);
                    const isActive = i === idx;
                    const RowIcon = item.icon || Icon;
                    return (
                      <button
                        key={`${kind}-${item.key}`}
                        type="button"
                        data-active={isActive}
                        onMouseEnter={() => setIdx(i)}
                        onClick={() => go(item)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isActive ? 'bg-primary-50' : 'hover:bg-surface-100'}`}
                      >
                        <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tone}`}><RowIcon className="w-4 h-4" /></span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-ink truncate">{item.title}</span>
                          {item.sub && <span className="block text-xs text-ink-secondary tabular-nums truncate">{item.sub}</span>}
                        </span>
                        {isActive && <CornerDownLeft className="w-4 h-4 text-primary-700 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Подсказки по клавишам */}
        <div className="hidden sm:flex items-center gap-4 px-5 h-11 border-t border-surface-200 bg-surface-100/60 text-xs text-ink-secondary shrink-0">
          <span className="inline-flex items-center gap-1.5"><Kbd>↑</Kbd><Kbd>↓</Kbd>{t('topbar.searchNavigate')}</span>
          <span className="inline-flex items-center gap-1.5"><Kbd>Enter</Kbd>{t('topbar.searchOpen')}</span>
          <span className="hidden md:inline-flex items-center gap-1.5"><Kbd>G</Kbd>+<Kbd>D</Kbd><Kbd>C</Kbd><Kbd>T</Kbd><Kbd>I</Kbd>… {t('topbar.hotkeysHint')}</span>
          <span className="inline-flex items-center gap-1.5 ml-auto"><Kbd>Esc</Kbd>{t('topbar.close')}</span>
        </div>
      </div>
    </div>
  );
}
